import { ISliderNavigationOptions } from './components/Navigation/ISliderNavigationOptions';
import { ISliderPaginationOptions } from './components/Pagination/ISliderPaginationOptions';

/**
 * Possible directions of the slider.
 */
export enum SliderDirection {
  Vertical = 'vertical',
  Horizontal = 'horizontal'
}

/**
 * Possible slide effects.
 */
export type SlideEffect = 'fade' | 'coverflow' | 'cube' | 'flip' | 'slide';

/**
 * The Slider autoplay options.
 */
export interface ISliderAutoplayOptions {
  /**
   * Is the autoplay enabled? If an object is provided to autoplay,
   * enabled is set to true by default.
   */
  enabled?: boolean;

  /** Should autoplay start over after reaching the end. */
  loop?: boolean;

  /** Autoplay delay in ms. */
  delay?: number;

  /** Should autoplay play in reverse? */
  reverse?: boolean;

  /** Should autoplay be disabled after user interacts with the slider. */
  disableOnInteraction?: boolean;
}
/**
 * Options for the custom slider
 * This is different from props, these are the "real" options for the
 * underlying slider engine. Props are transformed, evaluated, and merged with default options
 * to finalize an object that matches the ISliderOptions interface.
 *
 * Props can be a little different to provide more convenience.
 */
export interface ISliderOptions {
  /** Direction of the slider. */
  direction: SliderDirection;

  /** Height of the slider, can be any CSS value (250px, 50%). */
  height: string;

  /** Classname for the whole wrapper. */
  wrapperClassName: string;

  /** ClassName for the inner navigation wrapper. */
  navigationWrapperClassName: string;

  /** Classname for the slider itself. */
  sliderClassName: string;

  /**
   * Object describing the navigation.
   */
  navigation: ISliderNavigationOptions;

  /**
   * Object describing the pagination.
   */
  pagination: ISliderPaginationOptions;

  /**
   * Object describing the autoplay.
   */
  autoplay: ISliderAutoplayOptions;

  /** If true, then active slide will be centered. */
  centeredSlides: boolean;

  /**
   * How many slides to show at a time, can be a float
   * Can be combined with centeredSlides prop to create a cool slider
   * which shows 25% of prev and next slides. Should also allow auto.
   * */
  slidesPerView: number | 'auto';

  /**
   * How many slides to move at a time. Useful for loop / more than 1 slides per view.
   *
   * @default 1
   */
  slidesPerGroup?: number;

  /** Space between slides. Useful for loop / more than 1 slides per view. */
  spaceBetween: number;

  /** Loop infinitely? */
  loop: boolean;

  /**
   * Speed of the slide (transition) in milliseconds.
   * For a specified value N, this controls the following scenarios:
   * 1. User drags from one slide to another, no matter where the user releases
   * the slide, it will take N milliseconds to settle into its place.
   * 2. If autoplay is enabled, the slide itself will take N milliseconds
   * after which, the autoplay delay controls how much time is between the
   * end of one transition and initiation of another transition.
   * 3. If the user presses a pagination bullet or a navigation arrow, it will
   * take N milliseconds to transition to that slide.
   */
  transitionSpeedMS: number;

  /** Delay of the slide (transition) in milliseconds. */
  autoplayDelayMS: number;

  /** If true, grab cursor is displayed for Desktop. */
  grabCursor: boolean;

  /** Slide transition effect. */
  effect?: SlideEffect;

  /** Default active slide index. */
  initialSlide?: number;

  /**
   * If false, disable touch swiping.
   *
   * @default true
   * */
  allowTouchMove?: boolean;

  /**
   * If true, component is updated (reinitialized) each time you change its style (like hide/show).
   * This option Allows the slider to be initialized while offscreen.
   */
  observer?: boolean;

  /**
   * If true, also update component if parent container is changed. Must be
   * used with observer prop.
   */
  observeParents?: boolean;

  /** Is the slider paused. If unset it is false by default. */
  isPaused?: boolean;
}

/** Re-export for convenience. */
export type { ISliderNavigationOptions, ISliderPaginationOptions };
